// src/neetjee_guru/components/academicPlanner/BulkUploadModal.js
import React, { Component } from "react";
import { Modal, Button, Form, Table, Badge, ProgressBar } from "react-bootstrap";

/**
 * BulkUploadModal — Drag-and-drop CSV uploader for seeding academic_planner_details
 *
 * Expected CSV format:
 *   day_number, schedule_date, subject_name, topic_title, day_type, sub_type, exam_title, overall_status
 * Example:
 *   1, 2026-06-02, Mathematics, Matrices Part 1, Regular, , , PENDING
 *   6, 2026-06-07, Assessment, Weekly Test #01, Exam, Unit Test, Weekly Test #01, PENDING
 */

const SAMPLE_CSV_CONTENT = [
  "day_number,schedule_date,subject_name,topic_title,day_type,sub_type,exam_title,overall_status",
  "1,2026-06-02,Mathematics,Matrices Part 1,Regular,,,PENDING",
  "1,2026-06-02,Physics,Laws of Motion,Regular,,,PENDING",
  "2,2026-06-03,Chemistry,Atomic Structure,Regular,,,PENDING",
  "6,2026-06-07,Assessment,Weekly Test #01,Exam,Unit Test,Weekly Test #01,PENDING",
  "7,2026-06-08,Holiday,Sunday Holiday,Holiday,Institutional Holiday,,PENDING",
].join("\n");

function parseCSV(text) {
  // Robust RFC 4180 CSV Parser
  const parseLine = (line) => {
    const result = [];
    let cur = "";
    let inQuotes = false;
    for (let c = 0; c < line.length; c++) {
      const ch = line[c];
      if (ch === '"') {
        inQuotes = !inQuotes;
      } else if (ch === ',' && !inQuotes) {
        result.push(cur.trim());
        cur = "";
      } else {
        cur += ch;
      }
    }
    result.push(cur.trim());
    return result;
  };

  const lines = text.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
  if (lines.length < 2) return [];

  const headers = parseLine(lines[0]).map(h => h.toLowerCase().replace(/\s+/g, "_").replace(/^"|"$/g, ""));
  const rows = [];

  for (let i = 1; i < lines.length; i++) {
    const parts = parseLine(lines[i]);
    const row = {};
    headers.forEach((h, idx) => {
      let val = parts[idx] || "";
      if (val.startsWith('"') && val.endsWith('"')) {
        val = val.slice(1, -1);
      }
      row[h] = val.trim();
    });
    rows.push(row);
  }

  return rows;
}

const DAY_TYPE_BADGE = {
  Regular: "primary",
  Exam: "info",
  Holiday: "danger"
};

class BulkUploadModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isDragging: false,
      parsedRows: [],
      fileName: "",
      parseError: "",
      uploading: false,
      uploadProgress: 0,
      uploadDone: false,
      uploadError: ""
    };
    this.fileInputRef = React.createRef();
  }

  handleDragOver = (e) => {
    e.preventDefault();
    this.setState({ isDragging: true });
  };

  handleDragLeave = () => this.setState({ isDragging: false });

  handleDrop = (e) => {
    e.preventDefault();
    this.setState({ isDragging: false });
    const file = e.dataTransfer.files[0];
    if (file) this.processFile(file);
  };

  handleFileInput = (e) => {
    const file = e.target.files[0];
    if (file) this.processFile(file);
  };

  processFile = (file) => {
    if (!file.name.endsWith(".csv")) {
      this.setState({ parseError: "Only CSV files are accepted. Please upload a .csv file.", parsedRows: [], fileName: "" });
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const rows = parseCSV(e.target.result);
        if (rows.length === 0) {
          this.setState({ parseError: "CSV appears to be empty or malformed. Check the format.", parsedRows: [], fileName: file.name });
          return;
        }
        const required = ["day_number", "schedule_date", "subject_name", "topic_title", "day_type", "sub_type", "exam_title", "overall_status"];
        const keys = Object.keys(rows[0]).map(k => k.toLowerCase());
        const missing = required.filter(r => !keys.includes(r));
        if (missing.length > 0) {
          this.setState({ parseError: `Missing required columns: ${missing.join(", ")}`, parsedRows: [], fileName: file.name });
          return;
        }

        // Row-level validation check
        for (let idx = 0; idx < rows.length; idx++) {
          const r = rows[idx];
          const rowNum = idx + 2; // +1 for 1-based, +1 for header
          if (!r.day_number || isNaN(parseInt(r.day_number, 10))) {
            this.setState({ parseError: `Row ${rowNum}: Invalid or missing day_number '${r.day_number}'. Must be a number.`, parsedRows: [], fileName: file.name });
            return;
          }
          if (!r.schedule_date || !/^\d{4}-\d{2}-\d{2}$/.test(r.schedule_date)) {
            this.setState({ parseError: `Row ${rowNum}: Invalid schedule_date '${r.schedule_date}'. Must be in YYYY-MM-DD format.`, parsedRows: [], fileName: file.name });
            return;
          }
          // Allow empty subject_name for Holiday or Exam rows (defaults to General / Holiday)
          const isSpecialDay = r.day_type === "Holiday" || r.day_type === "Exam" || r.day_type === "Sunday";
          if (!isSpecialDay && (!r.subject_name || !r.subject_name.trim())) {
            this.setState({ parseError: `Row ${rowNum}: Missing subject_name for Regular day.`, parsedRows: [], fileName: file.name });
            return;
          }
          if (isSpecialDay && (!r.subject_name || !r.subject_name.trim())) {
            r.subject_name = r.day_type === "Holiday" ? "Holiday" : "General";
          }
        }

        this.setState({ parsedRows: rows, fileName: file.name, parseError: "", uploadDone: false, uploadError: "" });
      } catch (err) {
        this.setState({ parseError: "Failed to parse CSV: " + err.message, parsedRows: [], fileName: file.name });
      }
    };
    reader.readAsText(file);
  };

  downloadTemplate = () => {
    const blob = new Blob([SAMPLE_CSV_CONTENT], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "academic_planner_bulk_upload_template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  handleUpload = async () => {
    const { parsedRows } = this.state;
    const { plannerId, onBulkUpload, onHide } = this.props;
    if (!parsedRows.length) return;

    this.setState({ uploading: true, uploadProgress: 0, uploadError: "" });

    try {
      if (onBulkUpload) {
        // Simulate progress animation
        let prog = 0;
        const ticker = setInterval(() => {
          prog += 12;
          if (prog >= 90) clearInterval(ticker);
          this.setState({ uploadProgress: Math.min(prog, 90) });
        }, 150);

        await onBulkUpload({ plannerId, rows: parsedRows });

        clearInterval(ticker);
        this.setState({ uploadProgress: 100, uploading: false, uploadDone: true });
      }
    } catch (err) {
      this.setState({ uploading: false, uploadError: "Upload failed: " + err.message });
    }
  };

  handleClose = () => {
    this.setState({
      isDragging: false, parsedRows: [], fileName: "", parseError: "",
      uploading: false, uploadProgress: 0, uploadDone: false, uploadError: ""
    });
    this.props.onHide && this.props.onHide();
  };

  render() {
    const { show } = this.props;
    const { isDragging, parsedRows, fileName, parseError, uploading, uploadProgress, uploadDone, uploadError } = this.state;

    return (
      <Modal show={show} onHide={this.handleClose} centered size="xl" className="bulk-upload-modal">
        <Modal.Header closeButton style={{ background: "linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)", border: "none" }}>
          <Modal.Title className="h6 font-weight-bold m-0 text-white">
            <i className="fal fa-cloud-upload mr-2" /> Bulk Upload Syllabus Topics
            <span className="ml-2 font-weight-normal text-white-50 small">— CSV Import to academic_planner_details</span>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="p-4">
          {uploadDone ? (
            // ── Success State ─────────────────────────────────────────────────
            <div className="text-center py-5">
              <div style={{ fontSize: 64, color: "#10b981" }}><i className="fal fa-check-circle" /></div>
              <h5 className="font-weight-bold text-dark mt-3">Upload Successful!</h5>
              <p className="text-muted">{parsedRows.length} day records seeded into the academic planner database.</p>
              <button className="btn btn-primary font-weight-bold px-4" onClick={this.handleClose} style={{ borderRadius: 24 }}>
                Done
              </button>
            </div>
          ) : (
            <>
              {/* Template Download Banner */}
              <div className="d-flex align-items-center justify-content-between p-3 mb-4 rounded"
                style={{ background: "#f0fdf4", border: "1px solid #bbf7d0" }}>
                <div>
                  <strong className="text-success small"><i className="fal fa-file-csv mr-1" /> Download CSV Template</strong>
                  <p className="m-0 text-muted" style={{ fontSize: "12px" }}>
                    Required columns: <code>day_number, schedule_date, subject_name, topic_title, day_type, sub_type, exam_title, overall_status</code>
                  </p>
                </div>
                <button className="btn btn-sm btn-success font-weight-bold" onClick={this.downloadTemplate}
                  style={{ borderRadius: 20 }}>
                  <i className="fal fa-download mr-1" /> Template CSV
                </button>
              </div>

              {/* Drag & Drop Zone */}
              <div
                onClick={() => this.fileInputRef.current && this.fileInputRef.current.click()}
                onDragOver={this.handleDragOver}
                onDragLeave={this.handleDragLeave}
                onDrop={this.handleDrop}
                className="text-center py-5 mb-3"
                style={{
                  border: `2px dashed ${isDragging ? "#7c3aed" : "#cbd5e1"}`,
                  borderRadius: 12,
                  background: isDragging ? "#f5f3ff" : "#f8fafc",
                  cursor: "pointer",
                  transition: "all 0.2s ease"
                }}
              >
                <i className="fal fa-cloud-upload mb-3 d-block"
                  style={{ fontSize: 40, color: isDragging ? "#7c3aed" : "#94a3b8" }} />
                <p className="font-weight-bold text-dark m-0">
                  {isDragging ? "Drop your CSV file here" : "Drag & drop a CSV file, or click to browse"}
                </p>
                {fileName && (
                  <Badge variant="success" className="mt-2" style={{ fontSize: "13px", padding: "6px 12px" }}>
                    <i className="fal fa-file-alt mr-1" /> {fileName}
                  </Badge>
                )}
                <input ref={this.fileInputRef} type="file" accept=".csv" style={{ display: "none" }}
                  onChange={this.handleFileInput} />
              </div>

              {/* Parse Error */}
              {parseError && (
                <div className="alert alert-danger small mb-3">
                  <i className="fal fa-exclamation-triangle mr-1" /> {parseError}
                </div>
              )}

              {/* Upload Error */}
              {uploadError && (
                <div className="alert alert-danger small mb-3">
                  <i className="fal fa-times-circle mr-1" /> {uploadError}
                </div>
              )}

              {/* Uploading Progress */}
              {uploading && (
                <div className="mb-3">
                  <p className="small font-weight-bold text-primary mb-1">
                    <i className="fal fa-spinner fa-spin mr-1" /> Uploading {parsedRows.length} records to database...
                  </p>
                  <ProgressBar animated now={uploadProgress} style={{ height: 6, borderRadius: 4 }} />
                </div>
              )}

              {/* Preview Table */}
              {parsedRows.length > 0 && !uploading && (
                <div>
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <span className="font-weight-bold small text-dark">
                      <i className="fal fa-table mr-1 text-primary" /> Preview — {parsedRows.length} rows parsed
                    </span>
                    <span className="small text-muted">Showing first 10 rows</span>
                  </div>
                  <div style={{ maxHeight: 280, overflowY: "auto", border: "1px solid #e2e8f0", borderRadius: 8 }}>
                    <Table size="sm" className="mb-0" style={{ fontSize: "12px" }}>
                      <thead style={{ background: "#f8fafc", position: "sticky", top: 0 }}>
                        <tr>
                          <th className="py-2 px-3">#</th>
                          <th className="py-2">Day</th>
                          <th className="py-2">Date</th>
                          <th className="py-2">Subject</th>
                          <th className="py-2">Topic Title</th>
                          <th className="py-2">Type</th>
                          <th className="py-2">Sub Type</th>
                          <th className="py-2">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {parsedRows.slice(0, 10).map((row, idx) => (
                          <tr key={idx}>
                            <td className="px-3 text-muted">{idx + 1}</td>
                            <td className="font-weight-bold">{row.day_number}</td>
                            <td>{row.schedule_date}</td>
                            <td>{row.subject_name}</td>
                            <td style={{ maxWidth: 150, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {row.topic_title}
                            </td>
                            <td>
                              <Badge variant={DAY_TYPE_BADGE[row.day_type] || "secondary"} style={{ fontSize: "10px" }}>
                                {row.day_type}
                              </Badge>
                            </td>
                            <td>{row.sub_type || "-"}</td>
                            <td>{row.overall_status || "PENDING"}</td>
                          </tr>
                        ))}
                        {parsedRows.length > 10 && (
                          <tr>
                            <td colSpan={8} className="text-center text-muted py-2 small">
                              + {parsedRows.length - 10} more rows not shown in preview
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>
                </div>
              )}
            </>
          )}
        </Modal.Body>

        {!uploadDone && (
          <Modal.Footer className="d-flex justify-content-between">
            <button className="btn btn-sm btn-light" onClick={this.handleClose}>Cancel</button>
            <Button
              variant="primary"
              size="sm"
              className="font-weight-bold"
              disabled={parsedRows.length === 0 || uploading}
              onClick={this.handleUpload}
              style={{ borderRadius: 20, padding: "6px 20px" }}
            >
              {uploading
                ? <><i className="fal fa-spinner fa-spin mr-1" /> Uploading...</>
                : <><i className="fal fa-cloud-upload mr-1" /> Upload {parsedRows.length > 0 ? `${parsedRows.length} Records` : "CSV"}</>
              }
            </Button>
          </Modal.Footer>
        )}
      </Modal>
    );
  }
}

export default BulkUploadModal;
